import { CurrentShow } from '../../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { showInfo } from '../../../flows/showcase/functions/useCurrentShow/useCurrentShow.utils';
import {
  CreateLinkRequest,
  CreateShareTargetItemTypeEnum,
} from '../../../generated/api';
import { assertNever } from '../../../utils/typeHelper';
import { LinkServiceAcceptableShareTarget } from '../ShareTarget';

export type ServerActuallyExpectedShareTarget =
  | {
      type: CreateShareTargetItemTypeEnum.Profile;
      profileId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.Store;
      profileId: string;
      storeId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.LiveStream;
      liveStreamId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.Replay;
      liveStreamId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.UpcomingShow;
      upcomingShowId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.ListingLiveStream;
      listingId: string;
      liveStreamId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.ListingReplay;
      listingId: string;
      liveStreamId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.ListingUpcomingShow;
      listingId: string;
      storeId: string;
      upcomingShowId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.ListingEvergreen;
      listingId: string;
      storeId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.ListingPromotion;
      promotionId: string;
      storeId: string;
      listingId: string;
    }
  | {
      type:
        | CreateShareTargetItemTypeEnum.Event
        | CreateShareTargetItemTypeEnum.EventDiscovery;
      showEventId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.EventTicket;
      showEventId: string;
      ticketId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.HomePage;
    }
  | {
      type: CreateShareTargetItemTypeEnum.Promotion;
      promotionId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.LiveStreamGiveaway;
      liveStreamId: string;
      giveawayItemId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.UpcomingShowGiveaway;
      upcomingShowId: string;
      giveawayItemId: string;
    }
  | {
      type: CreateShareTargetItemTypeEnum.PopClip;
      popClipId: string;
      storeId: string;
    };

export function shareTargetToDynamicLinkRequest(
  shareTarget: LinkServiceAcceptableShareTarget,
): {
  serverShareTarget: CreateLinkRequest['shareTarget'];
} {
  let serverShareTarget: ServerActuallyExpectedShareTarget;

  switch (shareTarget.type) {
    case 'listing': {
      if (
        shareTarget.inShow.type === 'liveShow' ||
        shareTarget.inShow.type === 'upcomingShow' ||
        shareTarget.inShow.type === 'replay'
      ) {
        const { seller } = showInfo(shareTarget.inShow);
        const liveStreamId = showInfo(shareTarget.inShow).activeLiveStreamId;
        if (liveStreamId) {
          serverShareTarget = {
            type: CreateShareTargetItemTypeEnum.ListingLiveStream,
            listingId: shareTarget.listingId,
            liveStreamId,
          };
        } else if (shareTarget.inShow.type === 'upcomingShow') {
          serverShareTarget = {
            type: CreateShareTargetItemTypeEnum.ListingUpcomingShow,
            listingId: shareTarget.listingId,
            storeId: seller.id,
            upcomingShowId: (shareTarget.inShow as CurrentShow.UpcomingShow)
              .upcomingShow.id,
          };
        } else {
          serverShareTarget = {
            type: CreateShareTargetItemTypeEnum.ListingReplay,
            liveStreamId: shareTarget.inShow.liveStream.id,
            listingId: shareTarget.listingId,
          };
        }
      } else if (shareTarget.inShow.type === 'seller') {
        const { seller } = showInfo(shareTarget.inShow);
        serverShareTarget = {
          type: CreateShareTargetItemTypeEnum.ListingEvergreen,
          listingId: shareTarget.listingId,
          storeId: seller.id,
        };
      } else {
        assertNever(shareTarget.inShow);
      }
      break;
    }
    case 'giveaway': {
      const liveStreamId = showInfo(shareTarget.inShow).activeLiveStreamId;
      if (liveStreamId) {
        serverShareTarget = {
          type: CreateShareTargetItemTypeEnum.LiveStreamGiveaway,
          giveawayItemId: shareTarget.giveawayId,
          liveStreamId,
        };
      } else {
        serverShareTarget = {
          type: CreateShareTargetItemTypeEnum.UpcomingShowGiveaway,
          giveawayItemId: shareTarget.giveawayId,
          upcomingShowId: (shareTarget.inShow as CurrentShow.UpcomingShow)
            .upcomingShow.id,
        };
      }
      break;
    }
    case 'profile': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.Profile,
        profileId: shareTarget.profileId,
      };
      break;
    }
    case 'store': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.Store,
        storeId: shareTarget.storeId,
        profileId: shareTarget.storeId,
      };
      break;
    }
    case 'upcomingShow': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.UpcomingShow,
        upcomingShowId: shareTarget.upcomingShowId,
      };
      break;
    }
    case 'liveShow': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.LiveStream,
        liveStreamId: shareTarget.liveShowId,
      };
      break;
    }
    case 'replay': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.Replay,
        liveStreamId: shareTarget.liveShowId,
      };
      break;
    }
    case 'event': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.Event,
        showEventId: shareTarget.eventId,
      };
      break;
    }
    case 'eventTicket': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.EventTicket,
        ticketId: shareTarget.ticketId,
        showEventId: shareTarget.eventId,
      };
      break;
    }
    case 'promotion': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.Promotion,
        promotionId: shareTarget.promotionId,
      };
      break;
    }
    case 'popclip': {
      serverShareTarget = {
        type: CreateShareTargetItemTypeEnum.PopClip,
        popClipId: `${shareTarget.sellerId}_${shareTarget.clipId}`,
        storeId: shareTarget.sellerId,
      };
      break;
    }
    default: {
      assertNever(shareTarget);
    }
  }

  return {
    serverShareTarget,
  };
}
